import React from 'react';
import {
  AccountInfoResult,
  AuctionManagerEntryPoint,
  Deploy,
} from '@block-explorer/entities';
import {
  AuctionContractIcon,
  BodyText,
  FlexRow,
  useMatchMedia,
} from '@make-software/cspr-design';
import {
  Avatar,
  AvatarProps,
  HashFontSize,
  HistoryLink,
  TableDataAccountInfo,
} from '../../../components';
import { deriveAccountInfo } from '../../../utils/account';
import DeployFiatAmount from './DeployFiatAmount';
import { useDeployActionDataContext } from '../services/deploy-action-context';

const auctionActionNameMap = {
  [AuctionManagerEntryPoint.add]: 'Add',
  [AuctionManagerEntryPoint.withdraw]: 'Withdraw',
  [AuctionManagerEntryPoint.activate]: 'Activate',
  [AuctionManagerEntryPoint.delegate]: 'Delegate',
  [AuctionManagerEntryPoint.undelegate]: 'Undelegate',
  [AuctionManagerEntryPoint.redelegate]: 'Redelegate',
};

const AuctionContractIdentifier = ({
  identifier,
  contract_package_hash,
}: {
  identifier: string;
  contract_package_hash: string | undefined;
}) => {
  const { getContractPackagePath } = useDeployActionDataContext();
  return (
    <FlexRow align="center" itemsSpacing={8}>
      <Avatar
        path={AuctionContractIcon}
        size={'small'}
        alt={'Auction contract logo'}
      />
      <BodyText size={3} variation="blue" monotype>
        <HistoryLink
          href={getContractPackagePath(contract_package_hash)}
          ariaDescription={'Link to contract package details'}
        >
          {identifier}
        </HistoryLink>
      </BodyText>
    </FlexRow>
  );
};

interface ValidatorAccountInfoProps {
  publicKey: string | null;
  prefix: string;
  avatarSize?: AvatarProps['size'];
}

const ValidatorAccountInfo = ({
  publicKey,
  prefix,
  avatarSize,
}: ValidatorAccountInfoProps) => {
  const { getAccountInfo } = useDeployActionDataContext();

  const accountAvatarSize: AvatarProps['size'] =
    avatarSize == undefined
      ? useMatchMedia(['small', 'default'], [])
      : avatarSize;

  const accountInfo = getAccountInfo<AccountInfoResult>(publicKey);
  const validatorLogo = deriveAccountInfo(
    accountInfo?.account_info || accountInfo?.centralized_account_info
  );
  const validatorName = accountInfo?.account_info?.info?.owner?.name;

  return publicKey ? (
    <>
      <BodyText size={3} variation="darkGray">
        {prefix}
      </BodyText>
      <TableDataAccountInfo
        avatarSize={accountAvatarSize}
        logo={validatorLogo && validatorLogo?.logo}
        name={validatorName}
        publicKey={publicKey}
        loading={!publicKey}
        hashFontSize={HashFontSize.big}
      />
    </>
  ) : null;
};

const DefaultAuctionAction = ({ deploy }: { deploy: Deploy }) => (
  <FlexRow align={'center'} itemsSpacing={8}>
    <BodyText size={3} monotype variation={'black'}>
      {deploy.entryPoint?.name}
    </BodyText>
    <BodyText size={3} variation="darkGray">
      with
    </BodyText>
    <AuctionContractIdentifier
      identifier={'Auction System Contract'}
      contract_package_hash={deploy.contractPackage?.contract_package_hash}
    />
  </FlexRow>
);

const ManageAuctionBidAction = ({ deploy }: { deploy: Deploy }) => {
  const { args, timeTransactionCurrencyRate, contractPackage, entryPoint } =
    deploy;
  const amount = args.amount?.parsed as string;

  return (
    <FlexRow align={'center'} itemsSpacing={8}>
      <BodyText size={3} monotype variation={'black'}>
        {auctionActionNameMap[entryPoint?.name || '']}
      </BodyText>
      <AuctionContractIdentifier
        identifier={'Auction'}
        contract_package_hash={contractPackage?.contract_package_hash}
      />
      <BodyText size={3} variation="black" monotype>
        bid
      </BodyText>
      {amount && (
        <>
          <BodyText size={3} variation="darkGray">
            of
          </BodyText>
          <DeployFiatAmount
            amount={amount}
            rate={timeTransactionCurrencyRate}
          />
        </>
      )}
    </FlexRow>
  );
};

const DelegationAuctionAction = ({ deploy }: { deploy: Deploy }) => {
  const { timeTransactionCurrencyRate, entryPoint, args } = deploy;

  const amount = args.amount?.parsed as string;
  const initialValidatorPrefix =
    entryPoint?.name === AuctionManagerEntryPoint.delegate ? 'to' : 'from';

  return (
    <FlexRow align={'center'} gap={8}>
      <BodyText size={3} monotype variation={'black'}>
        {auctionActionNameMap[entryPoint?.name || '']}
      </BodyText>
      {amount && (
        <DeployFiatAmount amount={amount} rate={timeTransactionCurrencyRate} />
      )}
      <ValidatorAccountInfo
        publicKey={args.validator?.parsed as string}
        prefix={initialValidatorPrefix}
        avatarSize={'small'}
      />
      <ValidatorAccountInfo
        publicKey={args.new_validator?.parsed as string}
        prefix={'to'}
        avatarSize={'small'}
      />
    </FlexRow>
  );
};

const DeployActionAuction = ({ deploy }: { deploy: Deploy }) => {
  const { entryPoint } = deploy;

  const entryPointName = entryPoint?.name || '';

  const isManageAuctionBidAction =
    entryPointName === AuctionManagerEntryPoint.activate ||
    entryPointName === AuctionManagerEntryPoint.withdraw ||
    entryPointName === AuctionManagerEntryPoint.add;

  const isDelegationAction =
    entryPointName === AuctionManagerEntryPoint.delegate ||
    entryPointName === AuctionManagerEntryPoint.undelegate ||
    entryPointName === AuctionManagerEntryPoint.redelegate;

  if (isDelegationAction) {
    return <DelegationAuctionAction deploy={deploy} />;
  }

  if (isManageAuctionBidAction) {
    return <ManageAuctionBidAction deploy={deploy} />;
  }

  return <DefaultAuctionAction deploy={deploy} />;
};

export default DeployActionAuction;
